<script lang="ts" setup>
	import useProductActivationApi from '~/services/apis/product-activation'
	import type { NeedToKnowProps } from '~/components/Shared/need-to-know.vue'
	import type { BatchUploadSteps } from '~/components/Uploads/batch.vue'
	import { filterObject, replacePhoneNumberToNaija } from '~/utils/functions'
	import type { AddToPlanProps } from '~/pages/manage-custodian/[purchase_id]/add/new.vue'
	import type { DynamicTableRecord, RecordSchema, SelectOptionContext } from '~/types'

	const props = defineProps<AddToPlanProps>()

	const { urlQuery } = useUtils()
	const { StringToNumber } = useCurrency()
	const { addHealthCustodian } = useProductActivationApi()
	const { screenSizesBreak } = useMobile()

	const emits = defineEmits<{
		(event: 'update:form', payload: unknown[]): void
		(event: 'update:viewStaff', payload: boolean): void
		(event: 'done', response: NeedToKnowProps): void
	}>()
	const batchUploadGuide: BatchUploadSteps = {
		'Start here': {
			desc: 'Download the CSV/ XLS file template below',
			key: 'start'
		},
		'Fill Details': {
			desc: 'Fill in the CSV/ XLS file you just downloaded with your staff information which should include <span class="text-gray-600 font-medium">First name,Last name,Email address, Phone number, and the Number of Staff Dependants</span>.  You can use <a href="https://docs.google.com/spreadsheets" target="_blank" class="text-blue-500 font-medium">Google sheet</a> to edit the template or save it to your desktop or any folder on your PC and edit with excel software. See sample below:',
			key: 'details'
		},
		'Upload saved file ': {
			desc: 'Upload or drag & drop the saved CSV/ XLS file you just filled on your PC.',
			key: 'upload'
		}
	}
	const formToSubmit = computed(() => props.form)
	const fetchingSlots = shallowRef(false)
	const isSubmitting = shallowRef(false)
	const { getSupportContact } = useStore()
	const needToKnow = ref<NeedToKnowProps>([
		{
			title: 'Action required by the employee',
			description: 'Each staff member will receive an onboarding email to activate their health insurance plan.',
			icon: {
				dir: 'fill',
				name: 'people',
				pathClass: 'fill-purple-500',
				class: 'bg-purple-50'
			},
			descClass: 'text-gray-600'
		},
		{
			title: 'Accessing care at the hospital',
			description: 'Only staff with an activated plan can access healthcare services under this policy.',
			icon: {
				dir: 'other',
				name: 'shield',
				pathClass: 'fill-warning-500',
				class: 'bg-warning-50'
			},
			titleClass: 'text-warning-900',
			descClass: 'text-warning-500'
		},
		{
			title: 'Save this number',
			description: `This is your care co-ordinator <a href="tel:${getSupportContact('manager')?.line ?? 'n/a'}" class="text-main font-medium">${getSupportContact('manager')?.line ?? 'n/a'}</a>, kindly save this number for assistance if any issue arises.`,
			icon: {
				dir: 'fill',
				name: 'phone',
				class: 'bg-purple-50'
			},
			descClass: 'text-gray-500',
			titleClass: 'font-semibold'
		},
		{
			description: 'You can help your staff to activate their plan at any time',
			icon: {
				dir: 'fill',
				name: 'question-2',
				pathClass: 'fill-purple-500',
				class: 'bg-purple-50'
			},
			descClass: 'text-gray-600 italic'
		}
	])
	const planInformation = computed(() => {
		return props.purchaseInfo ?? null
	})
	const {
		planType,
		isFamilyPlan,
		familyRequirement: familyProps,
		totalDependantSlot,
		totalPendingDependant,
		hasDependant
	} = usePurchaseInfo({ planInformation })

	const totalDependantAdded = computed(() => {
		return formToSubmit.value.reduce((a, b) => {
			const k = StringToNumber(b.no_of_dependant as number) || 0
			return a + k
		}, 0)
	})
	const tableColumns = computed<TableColumn[]>(() => {
		const staffColumn: TableColumn[] = !props.uploadFromEmployees
			? [
					{
						key: 'first_name',
						dataIndex: 'first_name',
						title: 'First Name',
						class: 'min-w-[20%]'
					},
					{
						key: 'last_name',
						dataIndex: 'last_name',
						title: 'Last Name',
						class: 'min-w-[20%]'
					}
				]
			: [
					{
						key: 'employee_id',
						dataIndex: 'employee_id',
						title: props.viewStaff ? 'Staff' : 'Staff List',
						className: 'min-w-[200px]'
					}
				]

		let records = [
			{ key: 'sn', title: 'S/N', class: 'text-center' },
			...staffColumn,
			{ key: 'email', dataIndex: 'email', title: 'Email Address', class: 'min-w-[20%]' },
			{ key: 'phone_number', dataIndex: 'phone_number', title: 'Phone Number', class: 'min-w-[20%]' }
		]

		if (hasDependant.value) {
			records = [
				...records,
				{
					key: 'no_of_dependant',
					dataIndex: 'no_of_dependant',
					title: 'Dependants',
					class: 'min-w-[20%]'
				}
			]
		}
		return [...records, { key: 'action', title: 'Action' }]
	})
	const { attachStaffInfo } = useStaffList()
	const validateDependantValue = (_: unknown, value: number) => {
		const dependantValue = Number(totalPendingDependant.value ?? 0)
		const dependantSlot = dependantValue < 0 ? 0 : dependantValue

		if (isFamilyPlan.value) {
			let message = ''

			if (value < FAMILYPLANMIN) {
				message = `Staff dependant can not be less than ${FAMILYPLANMIN}`
			} else if (value > FAMILYPLANMAX) {
				message = `Staff dependant can not be higher than ${FAMILYPLANMAX}`
			}

			if (message !== '') {
				return Promise.reject(new Error(message))
			}
			return Promise.resolve()
		}

		if (value && totalDependantAdded.value <= totalDependantSlot.value && value <= totalDependantSlot.value) {
			return Promise.resolve()
		}
		const message = !dependantSlot
			? 'You have maxed out total dependant slot for plan'
			: `Dependants value can not be more than ${totalPendingDependant.value}`
		return Promise.reject(new Error(message))
	}
	const fieldValidator = (e: Record<string, DynamicTableRecord>, fn: Function) => {
		let errMsg = ''
		const totalSlotLeft = totalPendingDependant.value - totalDependantAdded.value
		const value = StringToNumber(String(e?.no_of_dependant?.value))
		if (value > totalSlotLeft) {
			errMsg = !totalSlotLeft
				? 'You have used up your dependant slot, replace with 0 to proceed.'
				: `You have total of ${totalSlotLeft} dependant slot, please reduce the dependant value to proceed.`
		}

		if (isFamilyPlan.value) {
			const dependantLeft = totalPendingDependant.value - StringToNumber(value)
			const noOfDependant = StringToNumber(e?.no_of_dependant?.value as number)
			const staffSlot = Number(planInformation.value?.pending_staff_slot ?? 0)
			if (noOfDependant < FAMILYPLANMIN) {
				errMsg = `Staff dependant must be at least minimum of ${FAMILYPLANMIN} dependants`
			} else if (noOfDependant > FAMILYPLANMAX) {
				errMsg = `Family policy must not be greater than ${FAMILYPLANMAX} dependants`
			} else if (dependantLeft === 0 && staffSlot > FAMILYPLANMIN) {
				errMsg =
					'You can not assign the whole dependant slot to a single record since you still have other slot left'
			}
		}
		const email = String(e?.email?.value ?? '')
		if (email && formToSubmit.value.some(item => String(item.email) === email)) {
			errMsg = `Staff with email ${email} is already added to the list`
		}

		if (errMsg) {
			useNotification().alert({
				message: errMsg,
				status: 400
			})
			fn(Promise.reject(errMsg))
		}
		fn(Promise.resolve(!errMsg))
		return null
	}
	const fetchSlot = async (_: RecordSchema) => {
		return resolve(async () => {
			const result = await props.fetchEmptySlot()
			if (result && Array.isArray(result?.items)) {
				const totalDependantLeft = totalPendingDependant.value - totalDependantAdded.value
				const list = result.items.map(item => {
					const dependants = isFamilyPlan.value ? FAMILYPLANMIN : Number(item?.no_of_dependant ?? 0)
					let policyType: Record<string, DynamicTableRecord> = {}
					let employeeList: Record<string, DynamicTableRecord> = {}

					if (props.uploadFromEmployees) {
						employeeList = {
							employee_id: {
								value: null,
								type: 'select',
								class: 'min-w-[150px]',
								searchable: true,
								options: [],
								validations: { required: true, message: 'Select a staff from the list.' }
							}
						}
					}

					if (isFamilyPlan.value) {
						policyType = {
							health_policy_type: {
								value: planType.value,
								type: 'select',
								class: 'min-w-[150px]',
								disabled: true
							}
						}
					}

					const result = {
						sn: true,
						temp_id: item.temp_id,
						no_of_dependant: {
							value: dependants,
							format: true,
							type: 'value',
							disabled: totalDependantLeft === 0,
							validations: {
								required: Boolean(dependants),
								validator: validateDependantValue
							}
						},
						...employeeList,
						first_name: {
							value: item?.first_name ?? '',
							disabled: item?.done,
							validations: {
								required: true,
								min: 3
							}
						},
						last_name: {
							value: item?.last_name ?? '',
							disabled: item?.done,
							validations: {
								required: true,
								min: 3
							}
						},
						phone_number: {
							value: item?.phone_number ?? '',
							type: 'tel',
							required: true,
							disabled: item?.done || props.uploadFromEmployees,
							validations: {
								required: true
							}
						},
						email: {
							value: item?.email ?? '',
							disabled: item?.done || props.uploadFromEmployees,
							validations: {
								type: 'email',
								required: true
							}
						},
						...policyType,
						isUsed: false,
						isActive: true,
						editable: true,
						done: false
					}
					return result
				})
				const { items, ...rest } = result
				return { ...rest, items: list }
			}
			return null
		}, fetchingSlots)
	}
	const proceedAfterSubmission = () => {
		// @ts-ignore
		emits('done', needToKnow.value)
	}
	const submitForm = async () => {
		const payload = {
			purchase_id: String(props.purchaseId),
			details: formToSubmit.value.map((item, i) => {
				const query = filterObject<{
					no_of_dependant: number
					phone_number: string
					first_name: string
					last_name: string
					temp_id: string
					email: string
				}>({ ...item, no_of_dependant: Number(item.no_of_dependant ?? 0), row_number: i + 1 }, [
					'done',
					'editable',
					'isActive',
					'isUsed',
					'sn',
					'loading',
					'key'
				])
				return { ...query, phone_number: replacePhoneNumberToNaija(query.phone_number) }
			})
		}
		resolve(async () => {
			const res = await addHealthCustodian(payload)
			if (res?.data) {
				useNotification().alert({
					status: 200,
					message: res?.responseText || ''
				})
				proceedAfterSubmission()
			}
		}, isSubmitting)
	}
</script>

<template>
	<section>
		<div v-if="urlQuery?.method === 'batch'">
			<div class="flex flex-col justify-center">
				<div
					v-if="isFamilyPlan"
					class="mx-auto mb-8 inline-flex w-full max-w-[800px] flex-col items-start justify-center gap-4 border border-[#9cd2c9] bg-[#f7fbfb] px-2 sm:px-4 sm:py-[17px]"
				>
					<div class="inline-flex items-center justify-start gap-2">
						<lazy-tools-icon dir="fill" icon="alert" :height="20" :width="20" />
						<div class="text-base font-semibold leading-tight text-[#4aa595]">
							Important Information about Policy type
						</div>
					</div>

					<div class="inline-flex flex-col items-start justify-start gap-1.5">
						<div class="self-stretch text-sm font-medium leading-tight text-[#475466]">
							To avoid errors in your upload, please note the following:
						</div>
						<div class="inline-flex items-center justify-start gap-2 self-stretch">
							<div class="h-1.5 w-1.5 rounded-full bg-[#4aa595]" />
							<div class="text-sm leading-relaxed text-[#475466]">
								<span class="font-semibold">Family Plan:</span>
								<span class="font-medium">
									Dependants added to a staff should be a minimum of {{ familyProps.min }} and maximum
									of {{ familyProps.max }}.</span
								>
							</div>
						</div>
						<div class="self-stretch text-sm font-medium leading-tight text-[#475466]">
							Ensure the Dependant size on your sheet matches the plan you’ve selected.
						</div>
					</div>
				</div>
				<forms-policy-activation-batch-upload
					:plan-id="String(props.purchaseId)"
					preview-img="/images/png/upload-staff-template.png"
					file-name="add-staff-template"
					:upload-steps="batchUploadGuide"
					@done="proceedAfterSubmission"
				/>
			</div>
		</div>
		<div v-else-if="!viewStaff">
			<div class="flex justify-center">
				<shared-highlight-note
					v-if="isFamilyPlan"
					note="importantNote"
					:ui="{
						wrapper: 'h-auto py-3 px-3 mt-10 bg-warning-100',
						text: 'text-warning-600'
					}"
				>
					<span class="font-[700] text-warning-600">Please Note:</span> With this plan, each staff member
					enrolled can include a minimum of {{ familyProps.min }} and a maximum of
					{{ familyProps.max }} dependants under them.
				</shared-highlight-note>
			</div>
			<uploads-add-staff-block
				v-if="!screenSizesBreak.biggerThanMd"
				:form="form"
				:data="fetchSlot"
				:skip-user-verification="Boolean(uploadFromEmployees)"
				:submitting="isSubmitting"
				@update:value="$emit('update:form', $event)"
				@validator="fieldValidator"
				@done="submitForm"
			/>
			<uploads-add-staff-table
				v-else
				:records="fetchSlot"
				:table-columns="tableColumns"
				:loading="loading || fetchingSlots"
				:submitting="isSubmitting"
				:deletable="false"
				file-name="add-staff-template"
				class="mt-10 w-full"
				:skip-user-verification="Boolean(uploadFromEmployees)"
				:options="{ pagination: true, filter: { page: 1 } }"
				@update:value="$emit('update:form', $event)"
				@validator="fieldValidator"
				@done="submitForm"
			>
				<template #employee_id="{ column, record }">
					<forms-staff-list
						v-if="record?.['employee_id'] && uploadFromEmployees"
						v-model:value="(record.employee_id as DynamicTableRecord).value as string"
						placeholder="Pick from employee list"
						:purchase-id="purchaseId"
						:name="String(column ?? 'staff-list')"
						@handle-change="e => attachStaffInfo(e, record as Record<string, DynamicTableRecord>)"
					/>
				</template>
				<template #filled-employee_id="{ record }">
					{{ (record?.first_name as DynamicTableRecord)?.value ?? '' }}
					{{ (record?.last_name as DynamicTableRecord)?.value ?? '+1' }}
				</template>
			</uploads-add-staff-table>
		</div>
		<tables-policies
			v-else
			:columns="tableColumns"
			:records="(e: RecordSchema) => fetchEmptySlot(e)"
			:loading="fetchingSlots"
			class="mt-16"
		>
			<template #no_of_dependant="{ record }">
				{{ record?.meta?.no_of_dependant ?? 0 }}
			</template>
			<template #employee_id="{ record }">
				{{ record?.meta?.first_name ?? record?.first_name ?? 'N/A' }}
				{{ record?.meta?.last_name ?? record?.last_name ?? '' }}
			</template>
			<template #action>
				<button type="button" class="0 default-btn-sm mx-auto bg-primary-100 text-white" disabled>
					<lazy-tools-icon
						dir="fill"
						icon="check-2"
						:height="18"
						:width="18"
						path-class="stroke-white fill-transparent"
						class="mr-2"
					/>
					<span>Added</span>
				</button>
			</template>
		</tables-policies>
	</section>
</template>
